import { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCircleInfo, faFilePdf, faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import styles from "../WorkoutAdminModal.module.css";
import Badge from "./Badge";
import api from "../../services/api";
import { downloadPdf } from "../../utils/downloadPdf";
import { useToast } from "../ToastProvider";
import { useWorkoutAdminContext } from "../../context/WorkoutAdminContext";
import { useWorkoutPrograms } from "../../hooks/useWorkoutPrograms";
import StudentProgramAssign from "./StudentProgramAssign";
import StudentProgramHistory from "./StudentProgramHistory";

// Props removidas em favor de useWorkoutAdminContext() (issue 012,
// docs/07-issues.md) -- WorkoutAdminModal.jsx continua sendo o dono de todo
// esse estado, só não precisa mais listar as ~39 props manualmente aqui e
// no call site. Ver frontend/src/context/WorkoutAdminContext.js.
export default function StudentsTab() {
  const {
    filteredStudents,
    visibleStudents,
    professorsById,
    studentQuery,
    setStudentQuery,
    selectedStudentId,
    setSelectedStudentId,
    selectedStudent,
    planPickerRef,
    planPickerOpen,
    setPlanPickerOpen,
    planPickerQuery,
    setPlanPickerQuery,
    planPickerActiveIndex,
    setPlanPickerActiveIndex,
    planPickerResults,
    selectedAssignmentPlan,
    assignmentPlanId,
    setAssignmentPlanId,
    assignForm,
    setAssignForm,
    handleAssignPlanToStudent,
    saving,
    hasRationale,
    setRationaleModal,
    studentAssignments,
    setShowAIModal,
    setAiSuccess,
    setAiPreview,
    setAiHasAdjusted,
    setAiRationaleOpen,
    selectedStudentAssignmentId,
    setSelectedStudentAssignmentId,
    handleSyncStudentAssignment,
    handleRemoveStudentAssignment,
    setOpenStudentAssignmentModal,
    setDayVideosAssignment,
    setStudentAssignments,
    loadUserWorkoutPlans,
  } = useWorkoutAdminContext();

  const toast = useToast();
  const [pdfBusy, setPdfBusy] = useState(null); // chave do PDF em geração (evita clique duplo)
  const handleDownloadPdf = async (path, key) => {
    if (pdfBusy) return;
    setPdfBusy(key);
    try {
      await downloadPdf(api, path, "treino.pdf");
    } catch (e) {
      toast.error("Não foi possível gerar o PDF do treino. Tente novamente.");
    } finally {
      setPdfBusy(null);
    }
  };

  const { programs, assignProgram, loadHistory } = useWorkoutPrograms();
  // muda quando os treinos do aluno mudam, pra recarregar o histórico de
  // programas (remover/desativar o último treino encerra o programa)
  const assignmentsKey = studentAssignments.map((a) => `${a.id}:${a.is_active ? 1 : 0}`).join(",");
  const [programHistory, setProgramHistory] = useState([]);
  const [assignMode, setAssignMode] = useState("plan"); // "plan" | "program"

  const reloadProgramHistory = useCallback(async () => {
    if (!selectedStudent?.id) { setProgramHistory([]); return; }
    try {
      setProgramHistory(await loadHistory(selectedStudent.id));
    } catch (_) {
      setProgramHistory([]);
    }
  }, [selectedStudent?.id, loadHistory]);

  useEffect(() => { reloadProgramHistory(); }, [reloadProgramHistory, assignmentsKey]);

  return (
    <div className={`${styles.wamGrid} ${selectedStudentId ? styles.hasSelection : ""}`}>
                      <div className={styles.wamSidebar}>
                        <div className={styles.sectionHead}>
                          <div className={styles.sectionHeadLeft}>
                            <div className={styles.titleLine}>
                              <h3>Alunos</h3>
                              <Badge tone="info">{filteredStudents.length}/{visibleStudents.length}</Badge>
                            </div>
                            <p className={styles.sectionHint}>Selecione um aluno para relacionar treinos padrão.</p>
                          </div>
                        </div>

                        <div className={styles.sectionSubline}>
                          <input
                            className={styles.searchInput}
                            type="search"
                            placeholder="Buscar aluno"
                            value={studentQuery}
                            onChange={(e) => setStudentQuery(e.target.value)}
                            aria-label="Buscar aluno"
                          />
                        </div>

                        <div className={styles.wamList}>
                          {filteredStudents.map((student) => (
                            <button
                              key={student.id}
                              type="button"
                              className={`${styles.wamPlanItem} ${
                                selectedStudentId === student.id ? styles.activeItem : ""
                              }`}
                              aria-pressed={selectedStudentId === student.id}
                              onClick={() => setSelectedStudentId(student.id)}
                            >
                              <div className={styles.itemTop}>
                                <strong>{student.name}</strong>
                                {student._isSelf
                                  ? <Badge tone="brand">Eu</Badge>
                                  : <Badge tone="default">Aluno</Badge>}
                              </div>
                              <div className={styles.metaRow}>
                                {!student._isSelf && (() => {
                                  const profName = student.professor?.name || professorsById.get(student.professor_id)?.name;
                                  return profName ? <Badge tone="soft">{profName}</Badge> : null;
                                })()}
                              </div>
                            </button>
                          ))}

                          {!filteredStudents.length && (
                            <div className={styles.wamEmpty}>Nenhum aluno disponível.</div>
                          )}
                        </div>
                      </div>

                      <div className={styles.wamMain}>
                        {selectedStudent && (
                          <div className={styles.wamMobileBackBar}>
                            <button
                              type="button"
                              className={styles.wamMobileBackIcon}
                              aria-label="Voltar para alunos"
                              onClick={() => setSelectedStudentId(null)}
                            >
                              <FontAwesomeIcon icon={faArrowLeft} />
                            </button>
                            <strong>{selectedStudent.name}</strong>
                            <button
                              type="button"
                              className={styles.wamMobileSwitchBtn}
                              onClick={() => setSelectedStudentId(null)}
                            >
                              Trocar
                            </button>
                          </div>
                        )}
                        {selectedStudent ? (
                          <div className={styles.wamStack}>
                            <div className={styles.cardGlass}>
                              <div className={styles.sectionHead}>
                                <div className={styles.sectionHeadLeft}>
                                  <h4>{selectedStudent.name}</h4>
                                  <p className={styles.sectionHint}>
                                    {selectedStudent._isSelf
                                      ? "Crie e relacione treinos para o seu próprio perfil."
                                      : "Relacione treinos padrão e personalize apenas quando esse aluno precisar."}
                                  </p>
                                </div>
                                <div className={styles.infoTooltipWrap}>
                                  <button
                                    type="button"
                                    className={styles.infoTooltipTrigger}
                                    aria-label="Sugestão de uso"
                                    aria-describedby="usageHintTooltip"
                                  >
                                    <FontAwesomeIcon icon={faCircleInfo} />
                                  </button>
                                  <div className={styles.infoTooltipBubble} role="tooltip" id="usageHintTooltip">
                                    Relacione o treino padrão primeiro. Se precisar, abra o treino do aluno depois e ajuste séries, repetições, peso ou descanso só para ele.
                                  </div>
                                </div>
                              </div>

                              <div className={`${styles.assignLayout} ${styles.assignLayoutFull}`}>
                                <div className={styles.cardInner}>
                                  <div className={styles.assignModeSwitch} role="group" aria-label="O que atribuir">
                                    <button type="button" className={styles.assignModeBtn}
                                            aria-pressed={assignMode === "plan"} onClick={() => setAssignMode("plan")}>
                                      Um treino
                                    </button>
                                    <button type="button" className={styles.assignModeBtn}
                                            aria-pressed={assignMode === "program"} onClick={() => setAssignMode("program")}>
                                      Programa completo
                                    </button>
                                  </div>

                                  {assignMode === "program" ? (
                                    <StudentProgramAssign
                                      student={selectedStudent}
                                      programs={programs}
                                      history={programHistory}
                                      assignProgram={assignProgram}
                                      onAssigned={async () => {
                                        const assignments = await loadUserWorkoutPlans(selectedStudent.id);
                                        setStudentAssignments(assignments || []);
                                        await reloadProgramHistory();
                                      }}
                                    />
                                  ) : (<>
                                  <div className={styles.wamField}>
                                    <label htmlFor="assignPlanPicker">Treino padrão</label>
                                    <div className={styles.comboboxWrap} ref={planPickerRef}>
                                      <input
                                        id="assignPlanPicker"
                                        type="text"
                                        role="combobox"
                                        aria-expanded={planPickerOpen}
                                        aria-controls="assignPlanListbox"
                                        aria-autocomplete="list"
                                        autoComplete="off"
                                        className={styles.searchInput}
                                        placeholder="Buscar treino..."
                                        value={planPickerOpen ? planPickerQuery : (selectedAssignmentPlan?.title || "")}
                                        onFocus={() => { setPlanPickerOpen(true); setPlanPickerQuery(""); setPlanPickerActiveIndex(-1); }}
                                        onChange={(e) => { setPlanPickerQuery(e.target.value); setPlanPickerActiveIndex(-1); }}
                                        onKeyDown={(e) => {
                                          if (e.key === "ArrowDown") {
                                            e.preventDefault();
                                            setPlanPickerActiveIndex((i) => Math.min(i + 1, planPickerResults.length - 1));
                                          } else if (e.key === "ArrowUp") {
                                            e.preventDefault();
                                            setPlanPickerActiveIndex((i) => Math.max(i - 1, 0));
                                          } else if (e.key === "Enter") {
                                            e.preventDefault();
                                            const pick = planPickerResults[planPickerActiveIndex] || planPickerResults[0];
                                            if (pick) {
                                              setAssignmentPlanId(String(pick.id));
                                              setPlanPickerOpen(false);
                                              setPlanPickerQuery("");
                                            }
                                          } else if (e.key === "Escape") {
                                            setPlanPickerOpen(false);
                                            setPlanPickerQuery("");
                                          }
                                        }}
                                      />
                                      {planPickerOpen && (
                                        <div className={styles.comboboxList} role="listbox" id="assignPlanListbox">
                                          {!planPickerResults.length && (
                                            <div className={styles.comboboxEmpty}>Nenhum treino encontrado.</div>
                                          )}
                                          {planPickerResults.map((plan, idx) => (
                                            <button
                                              type="button"
                                              key={plan.id}
                                              role="option"
                                              aria-selected={String(plan.id) === String(assignmentPlanId)}
                                              className={`${styles.comboboxOption} ${
                                                idx === planPickerActiveIndex ? styles.comboboxOptionActive : ""
                                              } ${String(plan.id) === String(assignmentPlanId) ? styles.comboboxOptionSelected : ""}`}
                                              onMouseEnter={() => setPlanPickerActiveIndex(idx)}
                                              onClick={() => {
                                                setAssignmentPlanId(String(plan.id));
                                                setPlanPickerOpen(false);
                                                setPlanPickerQuery("");
                                              }}
                                            >
                                              {plan.title}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div className={styles.wamField}>
                                    <label>Nome personalizado <span style={{fontWeight:"normal",color:"var(--text-secondary)"}}>(opcional)</span></label>
                                    <input
                                      type="text"
                                      placeholder={selectedAssignmentPlan?.title || "Ex: Inferiores (Ênfase Quadríceps)"}
                                      value={assignForm.custom_name}
                                      onChange={(e) => setAssignForm((prev) => ({ ...prev, custom_name: e.target.value }))}
                                      maxLength={120}
                                    />
                                  </div>

                                  <div className={styles.wamFormGrid}>
                                    <div className={styles.wamField}>
                                      <label>Sugestão do dia da semana</label>
                                      <select
                                        value={assignForm.day_of_week}
                                        onChange={(e) => setAssignForm((prev) => ({ ...prev, day_of_week: e.target.value }))}
                                      >
                                        <option value="">Sem sugestão</option>
                                        <option value="0">Segunda-feira</option>
                                        <option value="1">Terça-feira</option>
                                        <option value="2">Quarta-feira</option>
                                        <option value="3">Quinta-feira</option>
                                        <option value="4">Sexta-feira</option>
                                        <option value="5">Sábado</option>
                                        <option value="6">Domingo</option>
                                      </select>
                                    </div>
                                  </div>

                                  <label className={styles.wamCheck}>
                                    <input
                                      type="checkbox"
                                      checked={assignForm.is_active}
                                      onChange={(e) =>
                                        setAssignForm((prev) => ({ ...prev, is_active: e.target.checked }))
                                      }
                                    />
                                    <span>Vínculo ativo</span>
                                  </label>

                                  <div className={styles.actionsRow}>
                                    <button
                                      type="button"
                                      className={`${styles.wamBtn} ${styles.primary}`}
                                      onClick={handleAssignPlanToStudent}
                                      disabled={saving || !assignmentPlanId}
                                    >
                                      Relacionar treino
                                    </button>
                                  </div>
                                  </>)}
                                </div>

                              </div>
                            </div>

                            <div className={styles.cardGlass}>
                              <div className={styles.sectionHead}>
                                <div className={styles.sectionHeadLeft}>
                                  <h4>Treinos do aluno</h4>
                                  <p className={styles.sectionHint}>
                                    Aqui ficam os vínculos do aluno com opção de personalização individual.
                                  </p>
                                </div>
                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                  {hasRationale && (
                                    <button
                                      type="button"
                                      className={`${styles.wamBtn} ${styles.rationaleBtn}`}
                                      onClick={async () => {
                                        try {
                                          const res = await api.get(`/ai/student-workout-rationale/${selectedStudent?.id}`);
                                          if (res.data.rationale) setRationaleModal({ open: true, text: res.data.rationale });
                                        } catch (_) {}
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faCircleInfo} style={{ marginRight: 6 }} />
                                      Explicação do treino
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className={`${styles.wamBtn} ${styles.smallGhost}`}
                                    onClick={() => handleDownloadPdf(`/user-workout-plans/users/${selectedStudent?.id}/pdf`, "pdf-all")}
                                    disabled={!studentAssignments.length || pdfBusy === "pdf-all"}
                                    title="Baixar todos os treinos ativos do aluno em PDF, com a sua marca"
                                  >
                                    <FontAwesomeIcon icon={faFilePdf} style={{ marginRight: 6 }} />
                                    {pdfBusy === "pdf-all" ? "Gerando..." : "PDF do aluno"}
                                  </button>
                                  <button
                                    type="button"
                                    className={`${styles.wamBtn} ${styles.aiGenerateBtn}`}
                                    onClick={() => { setShowAIModal(true); setAiSuccess(null); setAiPreview(null); setAiHasAdjusted(false); setAiRationaleOpen(true); }}
                                  >
                                    <FontAwesomeIcon icon={faWandMagicSparkles} style={{ marginRight: 6 }} />
                                    {studentAssignments.length > 0 ? "Substituir com IA" : "Gerar com IA"}
                                  </button>
                                  <Badge tone="soft">{studentAssignments.length}</Badge>
                                </div>
                              </div>

                              <div className={styles.planItemList}>
                                {[...studentAssignments]
                                  .sort((a, b) => {
                                    const da = a.day_of_week ?? 7;
                                    const db_ = b.day_of_week ?? 7;
                                    if (da !== db_) return da - db_;
                                    return (a.custom_name || a.workout_plan?.title || "").localeCompare(
                                      b.custom_name || b.workout_plan?.title || "", "pt"
                                    );
                                  })
                                  .map((assignment) => (
                                  <div
                                    key={assignment.id}
                                    className={`${styles.planItemRow} ${
                                      selectedStudentAssignmentId === assignment.id ? styles.studentListRowSelected : ""
                                    }`}
                                  >
                                    <div className={styles.planItemRowMain}>
                                      <div className={styles.planItemTitle}>
                                        {assignment.custom_name || assignment.workout_plan?.title || `Treino #${assignment.workout_plan_id}`}
                                      </div>
                                      <div className={styles.planItemMeta}>
                                        <Badge tone={assignment.is_active ? "success" : "muted"}>
                                          {assignment.is_active ? "Ativo" : "Inativo"}
                                        </Badge>
                                        {assignment.program_name && (
                                          <Badge tone="brand">{assignment.program_name}</Badge>
                                        )}
                                        {assignment.day_of_week != null && (
                                          <Badge tone="info">
                                            {["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"][assignment.day_of_week]}
                                          </Badge>
                                        )}
                                        <span>
                                          {[
                                            assignment.start_date ? `Início ${assignment.start_date}` : null,
                                            assignment.end_date ? `Fim ${assignment.end_date}` : null,
                                            `${assignment.items?.length || 0} exercícios`,
                                          ]
                                            .filter(Boolean)
                                            .join(" • ")}
                                        </span>
                                      </div>
                                    </div>

                                    <div className={styles.planItemRowActions}>
                                      <button
                                        type="button"
                                        className={`${styles.wamBtn} ${styles.smallGhost}`}
                                        onClick={() => setDayVideosAssignment(assignment)}
                                        disabled={!assignment.items?.length}
                                        aria-label={`Ver vídeos de demonstração de ${assignment.custom_name || assignment.workout_plan?.title || "treino"}`}
                                      >
                                        ▶ Vídeos
                                      </button>
                                      <button
                                        type="button"
                                        className={`${styles.wamBtn} ${styles.smallGhost}`}
                                        onClick={() => { setSelectedStudentAssignmentId(assignment.id); setOpenStudentAssignmentModal(true); }}
                                      >
                                        Abrir
                                      </button>
                                      <button
                                        type="button"
                                        className={`${styles.wamBtn} ${styles.smallGhost}`}
                                        onClick={() => handleDownloadPdf(`/user-workout-plans/${assignment.id}/pdf`, `pdf-${assignment.id}`)}
                                        disabled={pdfBusy === `pdf-${assignment.id}`}
                                        aria-label={`Baixar PDF de ${assignment.custom_name || assignment.workout_plan?.title || "treino"}`}
                                      >
                                        {pdfBusy === `pdf-${assignment.id}` ? "Gerando..." : "PDF"}
                                      </button>
                                      <button
                                        type="button"
                                        className={`${styles.wamBtn} ${styles.smallGhost}`}
                                        onClick={() => handleSyncStudentAssignment(assignment.id)}
                                      >
                                        Sincronizar
                                      </button>
                                      <button
                                        type="button"
                                        className={`${styles.wamBtn} ${styles.smallDanger}`}
                                        onClick={() => handleRemoveStudentAssignment(assignment.id)}
                                      >
                                        Remover
                                      </button>
                                    </div>
                                  </div>
                                ))}

                                {!studentAssignments.length && (
                                  <div className={styles.wamEmpty}>
                                    Nenhum treino relacionado a este aluno ainda.
                                  </div>
                                )}
                              </div>
                            </div>

                            <StudentProgramHistory history={programHistory} />

                          </div>
                        ) : (
                          <div className={styles.cardGlass}>
                            <div className={styles.wamEmpty}>Selecione um aluno para começar.</div>
                          </div>
                        )}
                      </div>
                    </div>
  );
}
